<script>
    import { onMount } from "svelte";
    import * as d3 from "d3";

    export let data = []; // weekly data: [{week, count, court_id, state}, ...]
    export let highlightStates;
    export let wrapperClass = "";
    export let stateMapping = [];
    export let week_of;
    export let total_cases;

    const margin = { top: 20, right: 25, bottom: 30, left: 5 };

    // SVG vs chart dimensions
    let svgWidth = 800;
    let svgHeight = 400;
    let wrapperEl;
    let labelGroup;
    let highlight_states = highlightStates.map((h) => h.state_abbr);
    let chartWidth = svgWidth - margin.left - margin.right;
    let chartHeight = svgHeight - margin.top - margin.bottom;

    let svgEl, tooltipEl;

    let lastWidth = 0;

    const latestWeek = data.reduce((max, item) =>
        parseWeek(item.week) > max ? parseWeek(item.week) : max
        , parseWeek(data[0]?.week));

    // Parse week string like "2025-01-06/2025-01-12" to get start date
    function parseWeek(weekStr) {
        const startDate = weekStr.split('/')[0];
        return d3.timeParse("%Y-%m-%d")(startDate);
    }

    /* ----------------------------------------
     STATE LIST
  ---------------------------------------- */

    $: states = Array.from(
        new Set(data.map((d) => d.state).filter(Boolean)),
    ).sort();

    /* ----------------------------------------
     GROUPING
  ---------------------------------------- */

    // Aggregate weekly data by state and week, excluding the last week
    function aggregateByStateAndWeek(rows, state) {
        const stateRows = rows.filter(d => d.state === state);

        // Group by week and sum counts
        const rollup = d3.rollup(
            stateRows,
            (v) => d3.sum(v, d => d.count),
            (d) => d.week
        );

        const sorted = Array.from(rollup, ([week, count]) => ({
            date: parseWeek(week),
            week: week,
            count: count
        })).sort((a, b) => a.date - b.date);

        // Remove the last week of data
        return sorted;
    }

    // Build per-state series
    $: seriesByState = states
        .slice() // don't mutate original
        .sort((a, b) => {
            const aHighlighted = highlight_states.includes(a);
            const bHighlighted = highlight_states.includes(b);

            if (aHighlighted === bHighlighted) return 0;
            return aHighlighted ? 1 : -1; // highlighted go last
        })
        .map((state) => ({
            state,
            values: aggregateByStateAndWeek(data, state),
        }));

    $: dates = data.map((d) => parseWeek(d.week)).filter(Boolean);

    /* ----------------------------------------
     SCALES / AXES
  ---------------------------------------- */

    let x, y, xAxisGroup, yAxisGroup, gridLinesGroup;
    let lineGroup;

    function getWidth() {
        return wrapperEl.node().getBoundingClientRect().width;
    }

    function updateWidth() {
        const newWidth = getWidth();
        lastWidth = newWidth;

        svgWidth = newWidth;
        svgHeight = Math.max(newWidth * 0.3, 325);
        chartWidth = svgWidth - margin.left - margin.right;
        chartHeight = svgHeight - margin.top - margin.bottom;

        if (svgEl && data.length && seriesByState) updateChart();
    }

    // Add styles to axes, including colors and remove unwanted lines
    function styleAxis(g, isYAxis = false) {
        if (isYAxis) {
            // Remove the main axis line on Y
            g.select(".domain").remove();
            // Remove tick lines on y-axis
            g.selectAll(".tick line").remove();
        } else {
            g.select(".domain")
                .attr("stroke", "var(--border-color)")
                .attr("stroke-width", 1);

            g.selectAll(".tick line")
                .attr("stroke", "var(--border-color)")
                .attr("stroke-width", 1);
        }

        g.selectAll(".tick text")
            .style("font-size", "var(--scale-1)")
            .style("fill", "var(--gray-50)");
    }

    function createLine() {
        return d3
            .line()
            .x((d) => x(d.date))
            .y((d) => y(d.count))
            .curve(d3.curveMonotoneX);
    }

    /* ----------------------------------------
     INIT
    ---------------------------------------- */
    function initChart() {
        if (!data.length) return;

        const svg = d3.select(svgEl);
        svg.selectAll("*").remove();

        xAxisGroup = svg.append("g");
        yAxisGroup = svg.append("g");
        gridLinesGroup = svg.append("g").attr("class", "grid-lines");
        lineGroup = svg.append("g").attr("class", "state-lines");
        labelGroup = d3.select(svgEl).append("g").attr("class", "state-labels");

        updateChart();
    }

    /* ----------------------------------------
     UPDATE
    ---------------------------------------- */
    function updateChart() {
        if (!data.length || !seriesByState.length) return;

        const svg = d3.select(svgEl);

        const allDates = seriesByState[0].values;

        x = d3
            .scaleTime()
            .domain(d3.extent(dates))
            .range([margin.left, margin.left + chartWidth]);

        const yMax = d3.max(seriesByState, (s) =>
            d3.max(s.values, (d) => d.count),
        );

        y = d3
            .scaleLinear()
            .domain([0, yMax])
            .nice()
            .range([margin.top + chartHeight, margin.top]);

        // Generate ticks but skip the first one
        const xTicks = x.ticks(chartWidth < 600 ? 3 : 5).slice(1);

        xAxisGroup
            .attr("transform", `translate(0,${margin.top + chartHeight})`)
            .call(
                d3
                    .axisBottom(x)
                    .tickValues(xTicks)
                    .tickFormat(
                        chartWidth < 1000
                            ? d3.timeFormat("%b %Y")
                            : d3.timeFormat("%B %Y"),
                    )
                    .tickSizeOuter(0),
            )
            .call(styleAxis);

        const yTicks = y.ticks(3);
        const maxTick = d3.max(yTicks);
        yAxisGroup
            .attr("transform", `translate(${margin.left},0)`)
            .call(
                d3
                    .axisLeft(y)
                    .ticks(3)
                    .tickSizeOuter(0)
                    .tickFormat((d) => {
                        if (d <= 0) return "";
                        return d === maxTick
                            ? `${d.toLocaleString()} cases`
                            : `${d.toLocaleString()}`;
                    }),
            )
            .call((g) => styleAxis(g, true));

        // Reposition y-axis labels to appear above grid lines and extend rightward
        yAxisGroup
            .selectAll(".tick text")
            .attr("text-anchor", "start")
            .attr("x", 0)
            .attr("dy", "-0.5em");

        const gridLines = svg.select(".grid-lines");

        gridLines
            .selectAll("line")
            .data(yTicks)
            .join("line")
            .attr("x1", margin.left)
            .attr("x2", margin.left + chartWidth)
            .attr("y1", (d) => y(d))
            .attr("y2", (d) => y(d))
            .attr("stroke", "var(--border-color)")
            .attr("stroke-width", 1)
            .attr("opacity", 0.25);

        const line = createLine();

        // Helper to get original color for a state
        function getOriginalColor(d, hovered) {
            let matchState = highlightStates.filter(
                (h) => h.state_abbr == d.state,
            )?.[0];
            if (matchState) {
                return matchState.color;
            }
            return hovered ? "var(--gray-80)" : "var(--gray-40)";
        }

        // Helper to get state display name from stateMapping
        function getStateName(stateAbbr) {
            const mapping = stateMapping.find(
                (m) => m.state_abbreviation === stateAbbr,
            );
            return mapping?.state_name || stateAbbr.toUpperCase();
        }

        // Helper to get total cases for a state
        function getTotalCases(stateAbbr) {
            return d3.sum(data.filter((d) => d.state === stateAbbr), d => d.count);
        }

        // Helper to get last week info for a state
        function getLastWeekInfo(d) {
            const lastPoint = d.values[d.values.length - 1];
            if (lastPoint.date.getTime() != latestWeek.getTime()) return { count: 0, date: latestWeek };
            return {
                count: lastPoint.count,
                date: lastPoint.date
            };
        }

        // --- MULTIPLE STATE LINES ---
        const paths = lineGroup
            .selectAll("path")
            .data(seriesByState, (d) => d.state)
            .join("path")
            .attr("fill", "none")
            .attr("stroke", (d) => getOriginalColor(d))
            .attr("stroke-width", 2)
            .attr("opacity", 0.6)
            .attr("d", (d) => line(d.values))
            .style("pointer-events", "stroke")
            .on("mouseenter", function (event, d) {
                // Darken the hovered line
                d3.select(this)
                    .attr("stroke", getOriginalColor(d, true))
                    .attr("opacity", 1);

                // Fade all other lines
                paths.filter((p) => p.state !== d.state).attr("opacity", 0.2);

                const totalCases = getTotalCases(d.state);
                const lastWeekInfo = getLastWeekInfo(d);
                const stateName = getStateName(d.state);

                tooltipEl.style.opacity = 1;
                tooltipEl.innerHTML = `
          <div><strong>${stateName}</strong></div>
          <div>${total_cases}: ${totalCases.toLocaleString()}</div>
          <div>${week_of} ${d3.timeFormat("%b %d, %Y")(lastWeekInfo.date)}: ${lastWeekInfo.count.toLocaleString()}</div>
        `;
            })
            .on("mousemove", function (event) {

                const tooltipNode = tooltipEl;
                const tooltipWidth = tooltipNode.offsetWidth;
                const tooltipHeight = tooltipNode.offsetHeight;
                const pageWidth = window.innerWidth;
                const pageHeight = window.innerHeight + window.scrollY;

                // Initial left position logic
                let left = event.pageX + tooltipWidth + 20 > pageWidth
                ? event.pageX - tooltipWidth - 10
                : event.pageX + 10;

                // If tooltip would go off the left edge, center it horizontally
                if (left < 0) {
                    left = (pageWidth - tooltipWidth) / 2;
                }

                // Position above cursor if would go off bottom edge
                const top = event.pageY + tooltipHeight + 10 > pageHeight
                ? event.pageY - tooltipHeight - 10
                : event.pageY - 30;

                tooltipEl.style.left = `${left}px`;
                tooltipEl.style.top = `${top}px`;
            })
            .on("mouseleave", function (event, d) {
                // Reset the hovered line to original color and opacity
                d3.select(this)
                    .attr("stroke", getOriginalColor(d))
                    .attr("opacity", 0.6);

                // Reset all other lines to original opacity
                paths.attr("opacity", 0.6);

                tooltipEl.style.opacity = 0;
            });

        // --- LABELS FOR HIGHLIGHTED STATES ---
        const labelData = seriesByState.filter((s) =>
            highlightStates.some((h) => h.state_abbr === s.state),
        );

        // Clear and redraw labels (stroke behind, then fill on top)
        labelGroup.selectAll("*").remove();

        // Draw white stroke behind labels
        labelGroup.selectAll("text.label-stroke")
            .data(labelData, (d) => d.state)
            .join("text")
            .attr("class", "label-stroke")
            .attr("x", (d) => {
                const matchState = highlightStates.find(
                    (h) => h.state_abbr === d.state,
                );
                const lastPoint = d.values[d.values.length - 1];
                return x(lastPoint.date) + Number(matchState.offset_x);
            })
            .attr("y", (d) => {
                const matchState = highlightStates.find(
                    (h) => h.state_abbr === d.state,
                );
                const lastPoint = d.values[d.values.length - 1];
                return y(lastPoint.count) + Number(matchState.offset_y);
            })
            .text((d) => {
                const matchState = highlightStates.find(
                    (h) => h.state_abbr === d.state,
                );
                return matchState?.full_name;
            })
            .attr("fill", "none")
            .attr("stroke", "var(--background-color)")
            .attr("stroke-width", 3)
            .attr("stroke-linejoin", "round")
            .style("font-family", "var(--fonts-sans)")
            .style("font-size", "var(--scale-1)")
            .attr("text-anchor", (d) => {
                const matchState = highlightStates.find(
                    (h) => h.state_abbr === d.state,
                );
                return matchState?.align || "middle";
            });

        // Draw colored fill on top
        labelGroup.selectAll("text.label-fill")
            .data(labelData, (d) => d.state)
            .join("text")
            .attr("class", "label-fill")
            .attr("x", (d) => {
                const matchState = highlightStates.find(
                    (h) => h.state_abbr === d.state,
                );
                const lastPoint = d.values[d.values.length - 1];
                return x(lastPoint.date) + Number(matchState.offset_x);
            })
            .attr("y", (d) => {
                const matchState = highlightStates.find(
                    (h) => h.state_abbr === d.state,
                );
                const lastPoint = d.values[d.values.length - 1];
                return y(lastPoint.count) + Number(matchState.offset_y);
            })
            .text((d) => {
                const matchState = highlightStates.find(
                    (h) => h.state_abbr === d.state,
                );
                return matchState?.full_name;
            })
            .attr("fill", (d) => {
                const matchState = highlightStates.find(
                    (h) => h.state_abbr === d.state,
                );
                return matchState?.color || "black";
            })
            .style("font-family", "var(--fonts-sans)")
            .style("font-size", "var(--scale-1)")
            .attr("text-anchor", (d) => {
                const matchState = highlightStates.find(
                    (h) => h.state_abbr === d.state,
                );
                return matchState?.align || "middle";
            });
    }

    onMount(() => {
        wrapperEl = d3.select(wrapperClass);
        initChart();
        updateWidth();

        let resizeTimeout;
        const listener = () => {
            clearTimeout(resizeTimeout);
            resizeTimeout = setTimeout(updateWidth, 50);
        };

        window.addEventListener("resize", listener);
        return () => window.removeEventListener("resize", listener);
    });

    $: svgEl && data.length && seriesByState && updateChart();
</script>

<svg bind:this={svgEl} width={svgWidth} height={svgHeight}></svg>
<div class="tooltip" bind:this={tooltipEl}></div>
